import { ChatHandler } from '../../handlers/chat/ChatHandler';
import { useColyseusMessages } from './useColyseusMessages';
import { describeRewardBlock } from '../../../shared/quests/questRewardCapacity';

const purchaseFailures: Record<string, string> = {
  npc_not_found: 'That shop is no longer available. Talk to the shopkeeper again.',
  too_far: 'Move closer to the shopkeeper to buy this item.',
  item_not_available: 'This shop does not sell that item.',
  not_enough_gold: 'You do not have enough gold for this purchase.',
  inventory_full: 'Your inventory is full. Make room before buying this item.',
  invalid_data: 'The purchase could not be processed. Reopen the shop and try again.',
  error: 'The purchase failed. Please try again.',
};

const questFailures: Record<string, string> = {
  quest_not_found: 'This quest is no longer available.',
  already_accepted: 'You already have this quest. Check your quest log.',
  already_completed: 'You have already completed this quest.',
  level_too_low: 'Your level is too low for this quest.',
  missing_prerequisite: 'Complete the prerequisite quest first.',
  quest_not_offered: 'Nobody is offering this quest right now.',
  start_npc_not_found: 'The quest giver is not available here.',
  not_at_start_npc: 'Move closer to the quest giver to accept this quest.',
  quest_not_active: 'Accept this quest before trying to turn it in.',
  turn_in_npc_not_found: 'The quest turn-in contact is not available here.',
  not_at_turn_in_npc: 'Move closer to the quest turn-in contact.',
  reward_inventory_full: 'Make room for all quest rewards, then try again. Your quest progress and items have been kept.',
  objectives_incomplete: 'Complete all quest objectives before turning this quest in.',
};

export function actionFailureMessage(type: 'buyResult' | 'questError', payload: unknown): string | null {
  const message = payload !== null && typeof payload === 'object'
    ? payload as Record<string, unknown>
    : {};
  if (type === 'buyResult' && message.ok !== false) return null;
  const reason = typeof message.reason === 'string' ? message.reason : '';
  if (type === 'buyResult') {
    return Object.prototype.hasOwnProperty.call(purchaseFailures, reason)
      ? purchaseFailures[reason]
      : 'The purchase failed. Please try again.';
  }
  if (reason === 'level_too_low' && typeof message.requiredLevel === 'number'
      && Number.isInteger(message.requiredLevel) && message.requiredLevel > 0) {
    return `Reach level ${message.requiredLevel} before accepting this quest.`;
  }
  if (reason === 'reward_inventory_full' && Array.isArray(message.blockingItemIds)
      && message.blockingItemIds.length > 0) {
    return describeRewardBlock({
      blockingItemIds: message.blockingItemIds.filter((id): id is string => typeof id === 'string'),
      missingSlots: typeof message.missingSlots === 'number' ? message.missingSlots : undefined,
    });
  }
  return Object.prototype.hasOwnProperty.call(questFailures, reason)
    ? questFailures[reason]
    : 'The quest action failed. Please try again.';
}

/** Mounted once by the HUD; these failures use the existing local chat channel. */
export function useActionFailureFeedback(): void {
  const report = (type: 'buyResult' | 'questError', payload: unknown) => {
    const text = actionFailureMessage(type, payload);
    if (text) ChatHandler.getInstance().errorMessage(text);
  };
  useColyseusMessages({
    buyResult: (payload: unknown) => report('buyResult', payload),
    questError: (payload: unknown) => report('questError', payload),
  });
}
